import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { InstituicaoInput } from "@/components/InstituicaoInput";
import { GrauInput } from "@/components/GrauInput";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pencil, Link2, KeyRound, Trash2, X, Plus, Search, UserPlus, CheckCircle2, Ban } from "lucide-react";
import { toast } from "sonner";

const fmt = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return isNaN(d.getTime()) ? String(s).slice(0, 10) : d.toLocaleDateString("pt-BR");
};

export default function Instrutores() {
  const [instrutores, setInstrutores] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [listas, setListas] = useState({ grau: [], instituicao: [], pais: [] });
  const [editOpen, setEditOpen] = useState(false);
  const [vincOpen, setVincOpen] = useState(false);
  const [senhaOpen, setSenhaOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState({});
  const [novaSenha, setNovaSenha] = useState("");
  const [addTurma, setAddTurma] = useState("");
  const [delTarget, setDelTarget] = useState(null);
  const [delText, setDelText] = useState("");
  const [q, setQ] = useState("");
  const [filtro, setFiltro] = useState("todos");
  const [novoOpen, setNovoOpen] = useState(false);
  const [novoForm, setNovoForm] = useState({ validada: false });
  const [novoSaving, setNovoSaving] = useState(false);
  const [senhaProvisoria, setSenhaProvisoria] = useState(null);

  const load = () => api.get("/auth/instrutores").then((r) => setInstrutores(r.data));
  useEffect(() => {
    load();
    api.get("/turmas").then((r) => setTurmas(r.data));
    ["grau", "instituicao", "pais"].forEach((tipo) =>
      api.get(`/listas/${tipo}`).then((r) => setListas((s) => ({ ...s, [tipo]: r.data }))));
  }, []);

  const validar = async (i) => { await api.post(`/auth/instrutores/${i.id}/validar`); toast.success("Conta validada — acesso liberado"); load(); };
  const desativar = async (i) => { await api.post(`/auth/instrutores/${i.id}/desativar`); toast.success("Acesso desativado (sessões revogadas)"); load(); };
  const reativar = async (i) => { await api.post(`/auth/instrutores/${i.id}/reativar`); toast.success("Acesso reativado (exige novo login)"); load(); };
  const abrirNovo = () => { setNovoForm({ validada: false }); setNovoOpen(true); };
  const criarInstrutor = async () => {
    setNovoSaving(true);
    try {
      const instNome = (novoForm.instituicao_nome || "").trim();
      const instMatch = listas.instituicao.find((it) => (it.nome || "").toLowerCase() === instNome.toLowerCase());
      const grauNome = (novoForm.grau_nome || "").trim();
      const grauMatch = listas.grau.find((g) => (g.nome || "").toLowerCase() === grauNome.toLowerCase());
      const { data } = await api.post("/auth/instrutores", {
        nome: novoForm.nome, nome_guerra: novoForm.nome_guerra, email: novoForm.email,
        grau_id: grauMatch ? grauMatch.id : null, grau_nome: grauNome || null,
        instituicao_nome: instNome || null, instituicao_id: instMatch ? instMatch.id : "",
        unidade: novoForm.unidade || "", observacoes: novoForm.observacoes || "",
        password: novoForm.password || null, validada: !!novoForm.validada,
      }, { silent: true });
      setNovoOpen(false);
      if (data.senha_provisoria) setSenhaProvisoria({ email: data.email, senha: data.senha_provisoria });
      else toast.success("Instrutor criado");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setNovoSaving(false); }
  };

  const openEdit = (i) => {
    setCurrent(i);
    setForm({ nome: i.name || "", nome_guerra: i.nome_guerra || "", grau_nome: i.grau_nome || "", instituicao_nome: i.instituicao_nome || "", unidade: i.unidade || "", pais_id: i.pais_id || "", rg: i.rg || "", matricula: i.matricula || "", identificacao_funcional: i.identificacao_funcional || "", telefone: i.telefone || "" });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    try {
      const instNome = (form.instituicao_nome || "").trim();
      const instMatch = listas.instituicao.find((it) => (it.nome || "").toLowerCase() === instNome.toLowerCase());
      const grauNome = (form.grau_nome || "").trim();
      const grauMatch = listas.grau.find((g) => (g.nome || "").toLowerCase() === grauNome.toLowerCase());
      await api.put(`/auth/instrutores/${current.id}`, {
        nome: form.nome, nome_guerra: form.nome_guerra || "",
        grau_id: grauMatch ? grauMatch.id : null, grau_nome: grauNome || null,
        instituicao_nome: instNome || null, instituicao_id: instMatch ? instMatch.id : "",
        unidade: form.unidade, pais_id: form.pais_id || null,
        rg: form.rg || "", matricula: form.matricula, identificacao_funcional: form.identificacao_funcional || "", telefone: form.telefone,
      });
      toast.success("Instrutor atualizado");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openVinc = (i) => { setCurrent(i); setAddTurma(""); setVincOpen(true); };
  const refreshCurrent = async (id) => {
    const { data } = await api.get(`/auth/instrutores/${id}`);
    setCurrent(data);
    load();
  };
  const vincular = async () => {
    if (!addTurma) return;
    await api.post(`/auth/instrutores/${current.id}/turmas/${addTurma}`);
    setAddTurma("");
    toast.success("Vínculo adicionado");
    refreshCurrent(current.id);
  };
  const desvincular = async (turmaId) => {
    await api.delete(`/auth/instrutores/${current.id}/turmas/${turmaId}`);
    toast.success("Vínculo removido");
    refreshCurrent(current.id);
  };

  const openSenha = (i) => { setCurrent(i); setNovaSenha(""); setSenhaOpen(true); };
  const salvarSenha = async () => {
    try {
      await api.post(`/auth/instrutores/${current.id}/reset-senha`, { nova_senha: novaSenha });
      toast.success("Senha redefinida");
      setSenhaOpen(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const confirmExcluir = async () => {
    if (!delTarget) return;
    try {
      const { data } = await api.delete(`/auth/instrutores/${delTarget.id}`);
      const n = data.avaliacoes_preservadas || 0;
      toast.success(n ? `Instrutor excluído (${n} avaliação(ões) mantidas no histórico)` : "Instrutor excluído definitivamente");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setDelTarget(null);
    load();
  };

  const vincTurmasDisponiveis = turmas.filter((t) => !(current?.vinculos || []).some((v) => v.turma_id === t.id));
  const stOf = (i) => i.status || (i.ativo ? "ativo" : "pendente");
  const filtrados = instrutores.filter((i) => {
    if (filtro !== "todos" && stOf(i) !== filtro) return false;
    const t = `${i.name} ${i.nome_guerra || ""} ${i.email}`.toLowerCase();
    return !q || t.includes(q.toLowerCase());
  });

  return (
    <div>
      <PageHeader title="Gestão de Instrutores" subtitle="Crie, valide, edite, vincule e gerencie o acesso dos instrutores" />

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="busca-instrutor" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar por nome, nome de guerra ou e-mail" className="pl-9" />
        </div>
        <div className="flex items-center gap-1" data-testid="filtros-status">
          {["todos", "pendente", "ativo", "desativado"].map((f) => (
            <Button key={f} size="sm" variant={filtro === f ? "default" : "outline"} data-testid={`filtro-${f}`} onClick={() => setFiltro(f)} className="capitalize">
              {f === "todos" ? "Todos" : f === "pendente" ? "Pendentes" : f === "ativo" ? "Ativos" : "Desativados"}
            </Button>
          ))}
        </div>
        <Button data-testid="novo-instrutor-btn" onClick={abrirNovo}><UserPlus className="h-4 w-4 mr-2" />Novo instrutor</Button>
      </div>

      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Nome</TableHead>
              <TableHead className="hidden md:table-cell">E-mail</TableHead>
              <TableHead className="hidden lg:table-cell">Grau</TableHead>
              <TableHead className="hidden lg:table-cell">Instituição</TableHead>
              <TableHead className="hidden xl:table-cell">Unidade</TableHead>
              <TableHead className="hidden xl:table-cell">País</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden md:table-cell">Cadastro</TableHead>
              <TableHead className="hidden lg:table-cell">Último acesso</TableHead>
              <TableHead className="hidden md:table-cell">Vínculos</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtrados.map((i) => (
              <TableRow key={i.id} data-testid={`instrutor-row-${i.id}`} className="border-border">
                <TableCell className="font-medium">{i.name}</TableCell>
                <TableCell className="hidden md:table-cell text-muted-foreground text-xs">{i.email}</TableCell>
                <TableCell className="hidden lg:table-cell text-muted-foreground text-xs">{i.grau_nome || "—"}</TableCell>
                <TableCell className="hidden lg:table-cell text-muted-foreground text-xs">{i.instituicao_nome || "—"}</TableCell>
                <TableCell className="hidden xl:table-cell text-muted-foreground text-xs">{i.unidade || "—"}</TableCell>
                <TableCell className="hidden xl:table-cell text-muted-foreground text-xs">{i.pais_nome || "—"}</TableCell>
                <TableCell>
                  {(() => {
                    const st = stOf(i);
                    const m = { ativo: ["bg-emerald-500/20 text-emerald-300 border-emerald-500/40", "ATIVO"], pendente: ["bg-amber-500/20 text-amber-300 border-amber-500/40", "PENDENTE"], desativado: ["bg-red-500/20 text-red-300 border-red-500/40", "DESATIVADO"] };
                    const [cls, label] = m[st] || m.pendente;
                    return <Badge className={cls} data-testid={`status-${i.id}`}>{label}</Badge>;
                  })()}
                </TableCell>
                <TableCell className="hidden md:table-cell text-muted-foreground font-mono text-xs">{fmt(i.created_at)}</TableCell>
                <TableCell className="hidden lg:table-cell text-muted-foreground font-mono text-xs">{fmt(i.last_login_at)}</TableCell>
                <TableCell className="hidden md:table-cell">
                  <div className="flex flex-wrap gap-1">
                    {(i.vinculos || []).slice(0, 2).map((v) => <Badge key={v.turma_id} className="bg-secondary text-secondary-foreground border-border text-[10px]">{v.turma_nome}</Badge>)}
                    {(i.vinculos || []).length > 2 && <Badge className="bg-secondary text-muted-foreground text-[10px]">+{i.vinculos.length - 2}</Badge>}
                    {!(i.vinculos || []).length && <span className="text-muted-foreground text-xs">—</span>}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-2">
                    {stOf(i) === "ativo"
                      ? <button data-testid={`desativar-${i.id}`} onClick={() => desativar(i)} title="Desativar acesso" className="text-muted-foreground hover:text-amber-400"><Ban className="h-4 w-4" /></button>
                      : <button data-testid={`validar-${i.id}`} onClick={() => (stOf(i) === "desativado" ? reativar(i) : validar(i))} title={stOf(i) === "desativado" ? "Reativar acesso" : "Validar conta"} className="text-muted-foreground hover:text-emerald-400"><CheckCircle2 className="h-4 w-4" /></button>}
                    <button data-testid={`edit-instrutor-${i.id}`} onClick={() => openEdit(i)} className="text-muted-foreground hover:text-primary"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`vinc-instrutor-${i.id}`} onClick={() => openVinc(i)} className="text-muted-foreground hover:text-primary"><Link2 className="h-4 w-4" /></button>
                    <button data-testid={`senha-instrutor-${i.id}`} onClick={() => openSenha(i)} className="text-muted-foreground hover:text-primary"><KeyRound className="h-4 w-4" /></button>
                    <button data-testid={`del-instrutor-${i.id}`} onClick={() => { setDelTarget(i); setDelText(""); }} title="Excluir definitivamente" className="text-muted-foreground hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!filtrados.length && (
              <TableRow><TableCell colSpan={11} className="text-center py-10 text-muted-foreground">Nenhum instrutor encontrado.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Novo instrutor */}
      <Dialog open={novoOpen} onOpenChange={setNovoOpen}>
        <DialogContent className="bg-popover border-border">
          <DialogHeader><DialogTitle>Novo instrutor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Nome completo *</Label><Input data-testid="ni-nome" value={novoForm.nome || ""} onChange={(e) => setNovoForm({ ...novoForm, nome: e.target.value })} /></div>
              <div><Label>Nome de guerra *</Label><Input data-testid="ni-nome-guerra" value={novoForm.nome_guerra || ""} onChange={(e) => setNovoForm({ ...novoForm, nome_guerra: e.target.value })} /></div>
            </div>
            <div><Label>E-mail *</Label><Input data-testid="ni-email" type="email" value={novoForm.email || ""} onChange={(e) => setNovoForm({ ...novoForm, email: e.target.value })} /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Grau hierárquico</Label>
                <GrauInput value={novoForm.grau_nome} onChange={(v) => setNovoForm({ ...novoForm, grau_nome: v })} opcoes={listas.grau} testid="ni-grau" label={null} />
              </div>
              <div>
                <Label>Instituição</Label>
                <InstituicaoInput value={novoForm.instituicao_nome} onChange={(v) => setNovoForm({ ...novoForm, instituicao_nome: v })} opcoes={listas.instituicao} testid="ni-instituicao" label={null} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Unidade / Organização</Label><Input data-testid="ni-unidade" value={novoForm.unidade || ""} onChange={(e) => setNovoForm({ ...novoForm, unidade: e.target.value })} /></div>
              <div><Label>Senha (opcional)</Label><Input data-testid="ni-senha" type="text" value={novoForm.password || ""} onChange={(e) => setNovoForm({ ...novoForm, password: e.target.value })} placeholder="Vazio = gerar provisória" /></div>
            </div>
            <div><Label>Observações</Label><Input data-testid="ni-obs" value={novoForm.observacoes || ""} onChange={(e) => setNovoForm({ ...novoForm, observacoes: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch data-testid="ni-validada" checked={!!novoForm.validada} onCheckedChange={(v) => setNovoForm({ ...novoForm, validada: v })} />
              Já validar a conta (acesso liberado imediatamente)
            </label>
          </div>
          <DialogFooter><Button data-testid="ni-salvar" onClick={criarInstrutor} disabled={novoSaving || !novoForm.nome || !novoForm.nome_guerra || !novoForm.email}>Criar instrutor</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Senha provisória (exibida uma única vez) */}
      <Dialog open={!!senhaProvisoria} onOpenChange={(v) => { if (!v) setSenhaProvisoria(null); }}>
        <DialogContent className="bg-popover border-border" data-testid="senha-provisoria-dialog">
          <DialogHeader><DialogTitle>Senha provisória gerada</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <p className="text-muted-foreground">Anote e repasse com segurança. Ela será exibida <strong>apenas agora</strong> e deverá ser trocada no primeiro acesso.</p>
            <div className="rounded-md border border-border p-3 font-mono">
              <div><span className="text-muted-foreground">E-mail:</span> {senhaProvisoria?.email}</div>
              <div data-testid="senha-provisoria-valor"><span className="text-muted-foreground">Senha:</span> <strong>{senhaProvisoria?.senha}</strong></div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="senha-provisoria-copiar" variant="outline" onClick={() => { navigator.clipboard?.writeText(senhaProvisoria?.senha || ""); toast.success("Senha copiada"); }}>Copiar</Button>
            <Button data-testid="senha-provisoria-ok" onClick={() => setSenhaProvisoria(null)}>Concluído</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editar */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-popover border-border">
          <DialogHeader><DialogTitle>Editar instrutor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Nome</Label><Input data-testid="ei-nome" value={form.nome || ""} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
              <div><Label>Nome de guerra</Label><Input data-testid="ei-nome-guerra" value={form.nome_guerra || ""} onChange={(e) => setForm({ ...form, nome_guerra: e.target.value })} placeholder="Ex.: Batalha" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Grau</Label>
                <GrauInput value={form.grau_nome} onChange={(v) => setForm({ ...form, grau_nome: v })} opcoes={listas.grau} testid="ei-grau" label={null} />
              </div>
              <div>
                <Label>Instituição</Label>
                <InstituicaoInput
                  value={form.instituicao_nome}
                  onChange={(v) => setForm({ ...form, instituicao_nome: v })}
                  opcoes={listas.instituicao}
                  testid="ei-instituicao"
                  label={null}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Unidade</Label><Input data-testid="ei-unidade" value={form.unidade || ""} onChange={(e) => setForm({ ...form, unidade: e.target.value })} /></div>
              <div>
                <Label>País</Label>
                <Select value={form.pais_id || ""} onValueChange={(v) => setForm({ ...form, pais_id: v })}>
                  <SelectTrigger data-testid="ei-pais"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {listas.pais.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>RG</Label><Input data-testid="ei-rg" value={form.rg || ""} onChange={(e) => setForm({ ...form, rg: e.target.value })} /></div>
              <div><Label>Identificação funcional</Label><Input data-testid="ei-idfuncional" value={form.identificacao_funcional || ""} onChange={(e) => setForm({ ...form, identificacao_funcional: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Matrícula</Label><Input data-testid="ei-matricula" value={form.matricula || ""} onChange={(e) => setForm({ ...form, matricula: e.target.value })} /></div>
              <div><Label>Telefone</Label><Input data-testid="ei-telefone" value={form.telefone || ""} onChange={(e) => setForm({ ...form, telefone: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter><Button data-testid="ei-salvar" onClick={saveEdit} disabled={!form.nome}>Salvar alterações</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vínculos */}
      <Dialog open={vincOpen} onOpenChange={setVincOpen}>
        <DialogContent className="bg-popover border-border">
          <DialogHeader><DialogTitle>Vínculos de {current?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Select value={addTurma} onValueChange={setAddTurma}>
                <SelectTrigger data-testid="vinc-select"><SelectValue placeholder="Selecione uma turma" /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {vincTurmasDisponiveis.map((t) => <SelectItem key={t.id} value={t.id}>{t.nome} — {t.curso_nome}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button data-testid="vinc-add-btn" onClick={vincular} disabled={!addTurma}><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {(current?.vinculos || []).map((v) => (
                <div key={v.turma_id} data-testid={`vinc-row-${v.turma_id}`} className="flex items-center justify-between gap-2 border border-border rounded-md p-2.5">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{v.turma_nome}</div>
                    <div className="text-xs text-muted-foreground">{v.curso_nome}</div>
                  </div>
                  <button data-testid={`vinc-remove-${v.turma_id}`} onClick={() => desvincular(v.turma_id)} className="text-muted-foreground hover:text-red-400"><X className="h-4 w-4" /></button>
                </div>
              ))}
              {!(current?.vinculos || []).length && <p className="text-sm text-muted-foreground">Nenhum vínculo. O instrutor não verá cursos/turmas até ser vinculado.</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset senha */}
      <Dialog open={senhaOpen} onOpenChange={setSenhaOpen}>
        <DialogContent className="bg-popover border-border">
          <DialogHeader><DialogTitle>Redefinir senha de {current?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Nova senha</Label>
            <Input data-testid="senha-input" type="text" value={novaSenha} onChange={(e) => setNovaSenha(e.target.value)} placeholder="Mínimo 4 caracteres" />
          </div>
          <DialogFooter><Button data-testid="senha-salvar" onClick={salvarSenha} disabled={novaSenha.length < 4}>Salvar senha</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmação de exclusão definitiva */}
      <AlertDialog open={!!delTarget} onOpenChange={(v) => { if (!v) { setDelTarget(null); setDelText(""); } }}>
        <AlertDialogContent className="bg-popover border-border" data-testid="instrutor-del-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir definitivamente o instrutor?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{delTarget?.name}</strong> · {delTarget?.email}<br />
              Ação <strong>irreversível</strong>: o login/perfil será removido e os vínculos com turmas serão desfeitos. As avaliações já realizadas permanecem no histórico com nome/grau preservados por snapshot. Avaliações em andamento impedem a exclusão. Para apenas suspender o acesso, use Desativar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label>Digite <strong>EXCLUIR</strong> para confirmar</Label>
            <Input data-testid="instrutor-del-input" value={delText} onChange={(e) => setDelText(e.target.value)} placeholder="EXCLUIR" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="instrutor-del-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="instrutor-del-confirmar" disabled={delText !== "EXCLUIR"} onClick={confirmExcluir} className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50">Excluir definitivamente</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
